// CharacterContextTriggerDemo.jsx
// -----------------------------------------------------------------------------
// Dev-only demo route for the inline ⓥ trigger + viewport-docked popover.
//
// Reachable at /dev/character-context-trigger (see router.jsx). Lets the
// operator verify the trigger behaviour in isolation — outside the
// opportunity marketplace — before reviewing on the live host pages.
//
// Each row simulates the kind of inline-with-character-name placement the
// real host pages use (OpportunityCard's "Character: …" line,
// OpportunitySpreadsheet's character cell, OpportunityDetails' header).
//
// Lives next to CharacterContextTrigger.jsx so the relationship is obvious
// to future maintainers.
// -----------------------------------------------------------------------------

import React from 'react';
import CharacterContextTrigger from './CharacterContextTrigger';

// Names chosen to exercise the deterministic-by-name cycling in
// useCharacterContext: four sample names that hash to each of the four
// fixtures (diana, cambitter, gate, bystander_three). The exact mapping is
// deterministic but not under our control — the value is just that you see
// four visually-distinct panels by clicking each ⓥ.
const DEMO_ROWS = [
  { id: 'demo-1', name: 'Sarah Pemberton' },
  { id: 'demo-2', name: 'Daniel Vance' },
  { id: 'demo-3', name: 'Miriam Lo' },
  { id: 'demo-4', name: 'Bystander Three' },
  { id: 'demo-5', name: 'Captain Wilcox' },
  { id: 'demo-6', name: 'Esme' },
];

const containerStyle = {
  padding: '32px 32px 64px',
  background: '#f0eee9',
  minHeight: '100vh',
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  color: '#2a2925',
};

const titleStyle = {
  fontFamily: '"Georgia", "Source Serif Pro", "Lora", serif',
  fontSize: 26,
  fontWeight: 600,
  margin: '0 0 6px',
};

const subtitleStyle = {
  fontSize: 13,
  color: '#6a6760',
  margin: '0 0 28px',
  lineHeight: 1.5,
  maxWidth: 680,
};

const sectionStyle = {
  marginBottom: 28,
  padding: '18px 20px',
  background: '#fff',
  borderRadius: 8,
  border: '1px solid #ebe6d8',
};

const sectionHeadStyle = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: '#6a6760',
  margin: '0 0 10px',
};

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '8px 0',
  borderBottom: '1px solid #f0ece0',
  fontSize: 14,
};

const CharacterContextTriggerDemo = () => (
  <div style={containerStyle}>
    <h1 style={titleStyle}>Character Context Trigger — dev preview</h1>
    <p style={subtitleStyle}>
      Click any <strong>ⓥ</strong> to slide the character-context panel in
      from the right. Try keyboard too: Tab to a trigger, Enter/Space to open,
      Esc to close, click outside to dismiss. Each name resolves
      deterministically to one of the four mock fixtures (Diana, Cambitter,
      Gate, Bystander Three), so the same name will always show the same
      panel — but the panel's header name will read as the live name, not the
      fixture's.
    </p>

    {/* Simulates the OpportunityCard "Character: {name}" line. */}
    <section style={sectionStyle}>
      <p style={sectionHeadStyle}>Simulated — OpportunityCard footer</p>
      {DEMO_ROWS.map((r) => (
        <div style={rowStyle} key={`card-${r.id}`}>
          <span>
            Character: <strong>{r.name}</strong>
          </span>
          <CharacterContextTrigger
            characterId={r.id}
            characterName={r.name}
          />
        </div>
      ))}
    </section>

    {/* Simulates the OpportunitySpreadsheet character cell. */}
    <section style={sectionStyle}>
      <p style={sectionHeadStyle}>Simulated — OpportunitySpreadsheet row</p>
      {DEMO_ROWS.map((r) => (
        <div style={rowStyle} key={`sheet-${r.id}`}>
          <strong>{r.name}</strong>
          <CharacterContextTrigger
            characterId={r.id}
            characterName={r.name}
          />
        </div>
      ))}
    </section>

    {/* Simulates the OpportunityDetails header. */}
    <section style={sectionStyle}>
      <p style={sectionHeadStyle}>Simulated — OpportunityDetails header</p>
      <div style={{ ...rowStyle, fontSize: 18 }}>
        <strong>{DEMO_ROWS[0].name}</strong>
        <CharacterContextTrigger
          characterId={DEMO_ROWS[0].id}
          characterName={DEMO_ROWS[0].name}
        />
      </div>
    </section>

    <p style={{ ...subtitleStyle, marginTop: 32 }}>
      <em>
        When the real resolver lands, flip USE_REAL_DATA in
        useCharacterContext.js. Callers — including this page — do not change.
      </em>
    </p>
  </div>
);

export default CharacterContextTriggerDemo;
